<script setup lang="ts">
import { computed } from 'vue'
import {
  SkyButton,
  SkyImage,
  SkyLink,
  useSkyBreakpoint,
} from '@groveco/skylight'
import type { ContentfulAsset } from '~/types/contentful'

const props = defineProps({
  headline: {
    type: String,
    required: true,
  },
  ctaText: {
    type: String,
    required: true,
  },
  ctaUrl: {
    type: String,
    required: true,
  },
  mobileImage: {
    type: Object as PropType<ContentfulAsset>,
    required: true,
  },
  mobileAltText: {
    type: String,
    required: true,
  },
  desktopImage: {
    type: Object as PropType<ContentfulAsset>,
    required: true,
  },
  desktopAltText: {
    type: String,
    required: true,
  },
  backgroundColor: {
    type: String,
    required: true,
  },
  titleCtaColor: {
    type: String,
    required: true,
  },
})

const { isMediumUp, isReady } = useSkyBreakpoint()

const mobileImageSrc = computed(() => {
  const mobileTransforms = 'fl_progressive,q_auto,g_west,c_fill,h_500'
  return transformImage(props.mobileImage?.url || '', mobileTransforms)
})

const desktopImageSrc = computed(() => {
  const desktopTransforms = 'fl_progressive,f_auto,g_west,q_auto,c_fill,h_1000'
  return transformImage(props.desktopImage?.url || '', desktopTransforms)
})

const imageSrc = computed(() => {
  return isMediumUp.value ? desktopImageSrc.value : mobileImageSrc.value
})

const imageAltText = computed(() => {
  return isMediumUp.value ? props.desktopAltText : props.mobileAltText
})

const imageClasses = computed(() => {
  const baseClass = 'ImageCtaHero_Image'
  return {
    [baseClass]: true,
    [`${baseClass}-ready`]: isReady.value,
    [`${baseClass}-desktop`]: isMediumUp.value,
  }
})

const styles = computed(() => {
  return {
    '--bg-color': props.backgroundColor,
    '--text-color': props.titleCtaColor,
  }
})
</script>

<template>
  <SkyLink :to="ctaUrl" unstyled class="ImageCtaHero" :style="styles">
    <SkyImage
      :class="imageClasses"
      data-test-id="image"
      :src="imageSrc"
      :alt="imageAltText"
    />
    <div class="ImageCtaHero_Wrapper">
      <div class="ImageCtaHero_Content">
        <h2 class="ImageCtaHero_Headline" data-test-id="headline">
          {{ headline }}
        </h2>
        <SkyButton outlined class="ImageCtaHero_Cta" data-test-id="cta">
          {{ ctaText }}
        </SkyButton>
      </div>
    </div>
  </SkyLink>
</template>

<style lang="scss">
.ImageCtaHero {
  border: none;
  background-color: var(--bg-color);
  display: grid;
  grid-template-columns: max-content;
  grid-template-rows: minmax(0, 1fr) minmax(0, auto);
  grid-template-areas: 'image wrapper';

  @include for-medium-up {
    min-height: 500px;
    grid-template-rows: 1fr;
    grid-template-columns: 1fr 1fr;
    grid-template-areas: 'image-desktop wrapper';
  }

  &_Wrapper {
    align-items: center;
    display: flex;
    grid-area: wrapper;
    grid-row: 2;
    margin: var(--spacing-4x) var(--spacing-4x) var(--spacing-8x);
    justify-content: center;

    @include for-medium-up {
      grid-row: unset;
      margin: var(--spacing-8x);
    }
  }

  &_Content {
    text-align: center;

    @include for-medium-up {
      text-align: left;
    }
  }

  &_Headline {
    @include type-brand-display('1');
    color: var(--text-color);
    max-width: 450px;

    @include for-medium-up {
      text-align: left;
    }
  }

  &_Image {
    grid-area: image;
    grid-column: 2;
    grid-row: 1;
    width: 100%;
    object-fit: cover;
    object-position: bottom;
    display: block;

    @include for-medium-up {
      display: none;
      &-ready {
        display: block;
      }
      height: 100%;
    }

    &-desktop {
      grid-area: image-desktop;
      object-position: 100%;
    }
  }

  &_Cta {
    margin-top: var(--spacing-4x);
    max-width: 280px;
    --text-color-action-primary: var(--text-color);
    --text-color-action-primary-hover: var(--text-color);
    --surface-color-default: var(--bg-color);
  }
}
</style>
